/**
 * Flaechenvorschlag (Kapitel D, "Automatischer Flaechenvorschlag").
 *
 * Zwei Quellen ergaenzen sich, und beide zusammen sind deutlich zuverlaessiger
 * als Bilderkennung allein:
 *
 *   Der Gebaeudeumriss liefert die Aussenkontur — saubere Ecken, rechte Winkel,
 *   bereits vereinfacht.
 *   Der Helligkeitsunterschied im Luftbild liefert die innere Teilung: Die
 *   beiden Seiten eines Satteldachs stehen unterschiedlich zur Sonne, die
 *   Firstlinie zeichnet sich als Helligkeitskante ab.
 *
 * Und die erkannte Firstlinie liefert nebenbei die Traufrichtung — womit der
 * Azimut ohne weiteren Klick feststeht (PV-D20).
 *
 * Alles hier ist reine Rechnung auf uebergebenen Helligkeitswerten. Woher die
 * kommen, weiss dieses Modul nicht — deshalb laeuft es auch auf dem Geraet
 * ohne Verbindung (PV-D23).
 */
import { bogenInGrad, gradInBogen } from './typen.js';
import { beschneideAnHalbebene, drehe, dreheRing, flaecheninhalt, huellkasten, kantenlaenge, punktInnerhalb, schwerpunkt, } from './polygon.js';
// -----------------------------------------------------------------------------
// Vereinfachen
// -----------------------------------------------------------------------------
/**
 * Douglas-Peucker auf einem geschlossenen Ring.
 *
 * Ohne diesen Schritt entstehen Polygone mit dreissig Stuetzpunkten, deren
 * Korrektur laenger dauert als das Zeichnen von Hand (PV-D21).
 */
export function vereinfache(ring, toleranzM = 0.3) {
    if (ring.length < 4)
        return [...ring];
    // Den Ring an zwei weit auseinanderliegenden Punkten aufschneiden, sonst
    // wuerde der Anfangspunkt das Ergebnis bestimmen.
    let fernster = 1;
    let groesster = -1;
    for (let i = 1; i < ring.length; i++) {
        const abstand = Math.hypot(ring[i].x - ring[0].x, ring[i].y - ring[0].y);
        if (abstand > groesster) {
            groesster = abstand;
            fernster = i;
        }
    }
    const vorne = douglasPeucker(ring.slice(0, fernster + 1), toleranzM);
    const hinten = douglasPeucker([...ring.slice(fernster), ring[0]], toleranzM);
    // Die gemeinsamen Endpunkte nicht doppelt uebernehmen.
    return [...vorne.slice(0, -1), ...hinten.slice(0, -1)];
}
function douglasPeucker(kette, toleranz) {
    if (kette.length < 3)
        return kette;
    const anfang = kette[0];
    const ende = kette[kette.length - 1];
    let index = 0;
    let groesster = 0;
    for (let i = 1; i < kette.length - 1; i++) {
        const abstand = abstandZurGeraden(kette[i], anfang, ende);
        if (abstand > groesster) {
            groesster = abstand;
            index = i;
        }
    }
    if (groesster <= toleranz)
        return [anfang, ende];
    return [
        ...douglasPeucker(kette.slice(0, index + 1), toleranz).slice(0, -1),
        ...douglasPeucker(kette.slice(index), toleranz),
    ];
}
function abstandZurGeraden(p, a, b) {
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const laenge = Math.hypot(dx, dy);
    if (laenge < 1e-12)
        return Math.hypot(p.x - a.x, p.y - a.y);
    return Math.abs((p.x - a.x) * dy - (p.y - a.y) * dx) / laenge;
}
// -----------------------------------------------------------------------------
// Begradigen
// -----------------------------------------------------------------------------
/**
 * Kanten auf rechte Winkel und Parallelitaet einrasten (PV-D21).
 *
 * Zuerst wird die vorherrschende Richtung des Gebaeudes bestimmt — ueber den
 * vierfachen Winkel, weil Kanten im Abstand von 90 Grad gleichwertig sind.
 * Danach wird in diesem gedrehten Rahmen ausgerichtet.
 */
export function begradige(ring, toleranzGrad = 15, durchlaeufe = 3, richtungBogen) {
    if (ring.length < 4)
        return [...ring];
    // Ohne Vorgabe die vorherrschende Richtung des Gebaeudes. Mit Vorgabe — etwa
    // der Traufkante — wird auf genau diese ausgerichtet: Das Modulraster laeuft
    // laengs der Traufe, also muessen First und Ortgang dazu passen und nicht zu
    // einer Mehrheit der Kanten.
    const richtung = richtungBogen ?? vorherrschendeRichtung(ring);
    const mitte = schwerpunkt(ring);
    let arbeit = dreheRing(ring, -richtung, mitte);
    for (let lauf = 0; lauf < durchlaeufe; lauf++) {
        const zielX = arbeit.map(() => []);
        const zielY = arbeit.map(() => []);
        for (let i = 0; i < arbeit.length; i++) {
            const j = (i + 1) % arbeit.length;
            const a = arbeit[i];
            const b = arbeit[j];
            const winkel = Math.abs(bogenInGrad(Math.atan2(b.y - a.y, b.x - a.x)));
            const zuWaagrecht = Math.min(winkel, Math.abs(180 - winkel));
            const zuSenkrecht = Math.abs(90 - winkel);
            if (zuWaagrecht <= toleranzGrad && zuWaagrecht <= zuSenkrecht) {
                const mittel = (a.y + b.y) / 2;
                zielY[i].push(mittel);
                zielY[j].push(mittel);
            }
            else if (zuSenkrecht <= toleranzGrad) {
                const mittel = (a.x + b.x) / 2;
                zielX[i].push(mittel);
                zielX[j].push(mittel);
            }
        }
        arbeit = arbeit.map((p, i) => ({
            x: mittelwert(zielX[i]) ?? p.x,
            y: mittelwert(zielY[i]) ?? p.y,
        }));
    }
    return dreheRing(arbeit, richtung, mitte);
}
function mittelwert(werte) {
    if (werte.length === 0)
        return undefined;
    return werte.reduce((summe, wert) => summe + wert, 0) / werte.length;
}
/** Die Richtung, in der das Gebaeude ueberwiegend steht, im Bogenmass. */
export function vorherrschendeRichtung(ring) {
    let x = 0;
    let y = 0;
    for (let i = 0; i < ring.length; i++) {
        const a = ring[i];
        const b = ring[(i + 1) % ring.length];
        const laenge = Math.hypot(b.x - a.x, b.y - a.y);
        if (laenge < 1e-9)
            continue;
        const winkel = Math.atan2(b.y - a.y, b.x - a.x);
        // Vierfacher Winkel: Kanten im Abstand von 90 Grad zeigen dann in dieselbe
        // Richtung und verstaerken einander, statt sich aufzuheben.
        x += Math.cos(4 * winkel) * laenge;
        y += Math.sin(4 * winkel) * laenge;
    }
    if (Math.hypot(x, y) < 1e-9)
        return 0;
    return Math.atan2(y, x) / 4;
}
/**
 * Den Umriss entlang der wahrscheinlichsten Firstlinie teilen.
 *
 * Geprueft werden nur Schnitte parallel zu vorhandenen Gebaeudekanten — ein
 * First laeuft praktisch immer parallel zu einer Traufe. Das haelt die Suche
 * klein und verhindert schiefe Vorschlaege, die niemand haben will.
 */
export function teileNachHelligkeit(umriss, probe, optionen = {}) {
    const lagen = optionen.lagen ?? 13;
    const mindestProben = optionen.mindestProben ?? 12;
    const mindestAnteil = optionen.mindestAnteil ?? 0.25;
    if (umriss.length < 3)
        return undefined;
    const gesamt = flaecheninhalt(umriss);
    if (gesamt <= 0)
        return undefined;
    let bester;
    let zweitbesterKontrast = 0;
    for (const richtung of kantenrichtungen(umriss)) {
        const normale = { x: -richtung.y, y: richtung.x };
        const projektionen = umriss.map((p) => p.x * normale.x + p.y * normale.y);
        const tief = Math.min(...projektionen);
        const hoch = Math.max(...projektionen);
        if (hoch - tief < 1e-6)
            continue;
        let besterDieserRichtung = 0;
        for (let i = 1; i <= lagen; i++) {
            const anteil = i / (lagen + 1);
            const lage = tief + (hoch - tief) * anteil;
            const auf = { x: normale.x * lage, y: normale.y * lage };
            const oben = beschneideAnHalbebene(umriss, auf, normale);
            const unten = beschneideAnHalbebene(umriss, auf, { x: -normale.x, y: -normale.y });
            if (oben.length < 3 || unten.length < 3)
                continue;
            if (flaecheninhalt(oben) / gesamt < mindestAnteil ||
                flaecheninhalt(unten) / gesamt < mindestAnteil) {
                continue;
            }
            const hellOben = mittlereHelligkeit(oben, probe);
            const hellUnten = mittlereHelligkeit(unten, probe);
            if (hellOben === undefined ||
                hellUnten === undefined ||
                hellOben.anzahl < mindestProben ||
                hellUnten.anzahl < mindestProben) {
                continue;
            }
            const kontrast = Math.abs(hellOben.mittel - hellUnten.mittel);
            if (kontrast > besterDieserRichtung)
                besterDieserRichtung = kontrast;
            if (bester && kontrast <= bester.kontrast)
                continue;
            const linie = schnittlinie(umriss, auf, normale);
            if (!linie)
                continue;
            bester = {
                teile: [oben, unten],
                firstlinie: linie,
                traufkanten: [
                    traufkanteGegenueber(oben, richtung, auf),
                    traufkanteGegenueber(unten, richtung, auf),
                ],
                kontrast,
                sicherheit: 0,
            };
        }
        if (bester && besterDieserRichtung < bester.kontrast) {
            zweitbesterKontrast = Math.max(zweitbesterKontrast, besterDieserRichtung);
        }
    }
    if (!bester)
        return undefined;
    bester.sicherheit = sicherheitAus(bester, zweitbesterKontrast, gesamt);
    return bester;
}
/**
 * Wie belastbar ist der Vorschlag?
 *
 * Drei Dinge gehen ein: wie deutlich der Helligkeitsunterschied ist, wie klar
 * er sich vom naechstbesten Schnitt in einer anderen Richtung abhebt, und wie
 * ausgewogen die beiden Haelften sind. Ein schlechter Vorschlag, der wie ein
 * Ergebnis aussieht, ist schlimmer als gar keiner.
 */
function sicherheitAus(vorschlag, zweitbester, gesamt) {
    const deutlichkeit = Math.min(1, vorschlag.kontrast / 25);
    const abstand = vorschlag.kontrast > 0 ? Math.min(1, (vorschlag.kontrast - zweitbester) / vorschlag.kontrast) : 0;
    const kleinere = Math.min(...vorschlag.teile.map((t) => flaecheninhalt(t)));
    const ausgewogen = Math.min(1, (kleinere / gesamt) * 2.5);
    return Math.max(0, Math.min(1, deutlichkeit * 0.55 + abstand * 0.25 + ausgewogen * 0.2));
}
/** Vorkommende Kantenrichtungen, zusammengefasst und nach Laenge geordnet. */
function kantenrichtungen(ring, zusammenfassenGrad = 8) {
    const gefunden = [];
    for (let i = 0; i < ring.length; i++) {
        const a = ring[i];
        const b = ring[(i + 1) % ring.length];
        const laenge = kantenlaenge(ring, i);
        if (laenge < 1e-6)
            continue;
        // Richtungen im Abstand von 180 Grad sind dieselbe Schnittrichtung.
        let winkel = Math.atan2(b.y - a.y, b.x - a.x);
        if (winkel < 0)
            winkel += Math.PI;
        const vorhanden = gefunden.find((eintrag) => Math.abs(eintrag.winkel - winkel) < gradInBogen(zusammenfassenGrad) ||
            Math.abs(Math.abs(eintrag.winkel - winkel) - Math.PI) < gradInBogen(zusammenfassenGrad));
        if (vorhanden)
            vorhanden.laenge += laenge;
        else
            gefunden.push({ winkel, laenge });
    }
    return gefunden
        .sort((links, rechts) => rechts.laenge - links.laenge)
        .slice(0, 4)
        .map((eintrag) => ({ x: Math.cos(eintrag.winkel), y: Math.sin(eintrag.winkel) }));
}
/** Raster ueber die Flaeche legen und mitteln, was innen liegt. */
function mittlereHelligkeit(flaeche, probe, ziel = 80) {
    const kasten = huellkasten(flaeche);
    const breite = kasten.maxX - kasten.minX;
    const hoehe = kasten.maxY - kasten.minY;
    if (breite <= 0 || hoehe <= 0)
        return undefined;
    const schritte = Math.max(4, Math.round(Math.sqrt(ziel)));
    let summe = 0;
    let anzahl = 0;
    for (let i = 0; i < schritte; i++) {
        for (let j = 0; j < schritte; j++) {
            const p = {
                x: kasten.minX + (breite * (i + 0.5)) / schritte,
                y: kasten.minY + (hoehe * (j + 0.5)) / schritte,
            };
            if (!punktInnerhalb(p, flaeche))
                continue;
            const wert = probe(p);
            if (wert === undefined)
                continue;
            summe += wert;
            anzahl++;
        }
    }
    return anzahl === 0 ? undefined : { mittel: summe / anzahl, anzahl };
}
/** Die beiden Endpunkte des Schnitts durch das Polygon. */
function schnittlinie(ring, auf, normale) {
    const richtung = { x: -normale.y, y: normale.x };
    const treffer = [];
    for (let i = 0; i < ring.length; i++) {
        const a = ring[i];
        const b = ring[(i + 1) % ring.length];
        const dA = (a.x - auf.x) * normale.x + (a.y - auf.y) * normale.y;
        const dB = (b.x - auf.x) * normale.x + (b.y - auf.y) * normale.y;
        if (dA === 0)
            treffer.push(a);
        if ((dA < 0 && dB > 0) || (dA > 0 && dB < 0)) {
            const t = dA / (dA - dB);
            treffer.push({ x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t });
        }
    }
    if (treffer.length < 2)
        return undefined;
    const laengs = treffer.map((p) => p.x * richtung.x + p.y * richtung.y);
    let kleinster = 0;
    let groesster = 0;
    for (let i = 1; i < treffer.length; i++) {
        if (laengs[i] < laengs[kleinster])
            kleinster = i;
        if (laengs[i] > laengs[groesster])
            groesster = i;
    }
    return [treffer[kleinster], treffer[groesster]];
}
/**
 * Welche Kante der Teilflaeche ist die Traufe?
 *
 * Die, die parallel zum First laeuft und am weitesten von ihm weg liegt — das
 * ist per Definition die untere Kante, zu der das Dach abfaellt. Gemessen wird
 * gegen die Firstlinie, nicht gegen den Schwerpunkt: sonst liegen die Schnitt-
 * und die Aussenkante gleich weit weg und es entscheidet der Zufall.
 */
function traufkanteGegenueber(teil, firstrichtung, aufDemFirst) {
    const normale = { x: -firstrichtung.y, y: firstrichtung.x };
    let besterIndex = 0;
    let bestesMass = -Infinity;
    for (let i = 0; i < teil.length; i++) {
        const a = teil[i];
        const b = teil[(i + 1) % teil.length];
        const laenge = Math.hypot(b.x - a.x, b.y - a.y);
        if (laenge < 1e-6)
            continue;
        const richtung = { x: (b.x - a.x) / laenge, y: (b.y - a.y) / laenge };
        const parallel = Math.abs(richtung.x * firstrichtung.x + richtung.y * firstrichtung.y);
        if (parallel < 0.7)
            continue;
        const kantenmitte = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
        const abstand = Math.abs((kantenmitte.x - aufDemFirst.x) * normale.x + (kantenmitte.y - aufDemFirst.y) * normale.y);
        const mass = abstand * laenge;
        if (mass > bestesMass) {
            bestesMass = mass;
            besterIndex = i;
        }
    }
    return besterIndex;
}
/** Nur zum Pruefen: einen Ring um seinen Schwerpunkt drehen. */
export const dreheUmMitte = (ring, grad) => ring.map((p) => drehe(p, gradInBogen(grad), schwerpunkt(ring)));
//# sourceMappingURL=umriss.js.map